import React, { useState, useEffect } from 'react';

export interface LightboxMetadata {
  id: string;
  type: 'moodboard' | 'change' | 'remove' | 'upload' | 'upstaging' | 'furniture';
  createdAt: string;
  prompt?: string;
  status?: string;
  // Additional metadata fields
  roomType?: string;
  roomStyle?: string;
  roomSize?: string;
  category?: string;
  tokensSpent?: number;
  processingId?: string;
  images?: Array<{
    id: string;
    image_url: string;
    image_name?: string;
    is_primary: boolean;
    sort_order: number;
  }>;
  // Upload fields
  originalFilename?: string;
  fileSize?: number;
  compressionRatio?: number;
  // Upstaging fields
  style?: string;
}

interface ImageLightboxProps {
  isOpen: boolean;
  imageUrl: string;
  alt: string;
  metadata?: LightboxMetadata;
  onClose: () => void;
}

const getBadgeStyles = (type: string) => {
  switch (type) {
    case 'moodboard':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'change':
      return 'bg-green-100 text-green-800 border-green-300';
    case 'remove':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'upload':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'upstaging':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const getBadgeIcon = (type: string) => {
  switch (type) {
    case 'moodboard':
      return '🎨';
    case 'change':
      return '✏️';
    case 'remove':
      return '🗑️';
    case 'upload':
      return '📤';
    case 'upstaging':
      return '🏠';
    default:
      return '🔧';
  }
};

const getBadgeText = (type: string) => {
  switch (type) {
    case 'moodboard':
      return 'Moodboard';
    case 'change':
      return 'AI Change';
    case 'remove':
      return 'AI Remove';
    case 'upload':
      return 'My Upload';
    case 'upstaging':
      return 'Restyle';
    case 'furniture_removal':
      return 'Furniture Removal';
    default:
      return 'Unknown';
  }
};

export const ImageLightbox: React.FC<ImageLightboxProps> = ({
  isOpen,
  imageUrl,
  alt,
  metadata,
  onClose
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Reset to first image when lightbox opens or metadata changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [isOpen, metadata?.id]);

  if (!isOpen) return null;

  // Determine if we have multiple images and get current image info
  const hasMultipleImages = metadata?.images && metadata.images.length > 1;
  const images = metadata?.images || [];
  const currentImage = hasMultipleImages ? images[currentImageIndex] : null;
  const displayImageUrl = currentImage ? currentImage.image_url : imageUrl;

  const handlePreviousImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    }
  };

  const handleNextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePreviousImage();
    } else if (e.key === 'ArrowRight') {
      handleNextImage();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    const downloadUrl = displayImageUrl;
    const fileName = currentImage?.image_name || 
      (metadata ? `${metadata.type}-${metadata.id}-${currentImageIndex + 1}.webp` : 'generation.webp');
    
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="relative max-w-7xl w-full max-h-[95vh] flex flex-col lg:flex-row bg-white rounded-lg overflow-hidden">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-10 w-10 flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white text-xl font-bold transition-all"
          title="Close"
        >
          ×
        </button>

        {/* Image container */}
        <div className="flex-1 flex items-center justify-center bg-gray-100 min-h-96 relative">
          <img 
            src={displayImageUrl} 
            alt={alt} 
            className="max-w-full max-h-[80vh] lg:max-h-[95vh] object-contain" 
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="12">Image not found</text></svg>';
            }}
          />

          {/* Navigation buttons for multiple images */}
          {hasMultipleImages && (
            <>
              <button
                onClick={handlePreviousImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white text-xl font-bold transition-all"
                title="Previous image"
              >
                ←
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full text-white text-xl font-bold transition-all"
                title="Next image"
              >
                →
              </button>

              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>

        {/* Metadata panel */}
        {metadata && (
          <div className="lg:w-80 bg-white p-6 border-l border-gray-200 overflow-y-auto">
            <div className="space-y-4">
              {/* Header with badge */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Generation Details</h3>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getBadgeStyles(metadata.type)}`}>
                  <span className="mr-1">{getBadgeIcon(metadata.type)}</span>
                  {getBadgeText(metadata.type)}
                </span>
              </div>

              {/* Basic info */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <p className="text-sm text-gray-900">
                    {new Date(metadata.createdAt).toLocaleString()}
                  </p>
                </div>

                {metadata.status && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <p className="text-sm text-gray-900 capitalize">{metadata.status}</p>
                  </div>
                )}

                {metadata.tokensSpent && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Tokens Used</label>
                    <p className="text-sm text-gray-900">{metadata.tokensSpent}</p>
                  </div>
                )}
              </div>

              {/* Multiple images thumbnails */}
              {hasMultipleImages && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">All Images ({images.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {images.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          index === currentImageIndex 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image.image_url}
                          alt={`${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%236b7280" font-size="8">Error</text></svg>';
                          }}
                        />
                        {image.is_primary && (
                          <div className="absolute top-1 left-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded">
                            Primary
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Type-specific metadata */}
              {metadata.type === 'moodboard' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Room Details</h4>
                  <div className="space-y-2">
                    {metadata.category && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Category</label>
                        <p className="text-sm text-gray-900">{metadata.category}</p>
                      </div>
                    )}
                    {metadata.roomType && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Room Type</label>
                        <p className="text-sm text-gray-900">{metadata.roomType}</p>
                      </div>
                    )}
                    {metadata.roomStyle && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Style</label>
                        <p className="text-sm text-gray-900">{metadata.roomStyle}</p>
                      </div>
                    )}
                    {metadata.roomSize && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Size</label>
                        <p className="text-sm text-gray-900">{metadata.roomSize}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI touchup prompt */}
              {(metadata.type === 'change' || metadata.type === 'remove') && metadata.prompt && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Prompt</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                    {metadata.prompt}
                  </p>
                </div>
              )}

              {/* Upload details */}
              {metadata.type === 'upload' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">File Details</h4>
                  <div className="space-y-2">
                    {metadata.originalFilename && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Original Filename</label>
                        <p className="text-sm text-gray-900 font-mono bg-gray-50 p-2 rounded">
                          {metadata.originalFilename}
                        </p>
                      </div>
                    )}
                    {metadata.fileSize && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">File Size</label>
                        <p className="text-sm text-gray-900">
                          {(metadata.fileSize / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    )}
                    {metadata.compressionRatio && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Compression</label>
                        <p className="text-sm text-gray-900">
                          Reduced by {metadata.compressionRatio.toFixed(1)}%
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Optimization</label>
                      <p className="text-sm text-gray-700">
                        Resized to 1024px longest side, converted to JPEG
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Upstaging details */}
              {metadata.type === 'upstaging' && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Upstaging Details</h4>
                  <div className="space-y-2">
                    {metadata.prompt && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Prompt</label>
                        <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                          {metadata.prompt}
                        </p>
                      </div>
                    )}
                    {metadata.style && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Style</label>
                        <p className="text-sm text-gray-900">{metadata.style}</p>
                      </div>
                    )}
                    {metadata.roomType && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500">Room Type</label>
                        <p className="text-sm text-gray-900">{metadata.roomType}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Technical details */}
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Technical Info</h4>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-500">ID</label>
                    <p className="text-xs text-gray-600 font-mono bg-gray-50 p-1 rounded">
                      {metadata.id}
                    </p>
                  </div>
                  {metadata.processingId && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500">Processing ID</label>
                      <p className="text-xs text-gray-600 font-mono bg-gray-50 p-1 rounded">
                        {metadata.processingId}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="border-t pt-4 space-y-2">
                <button
                  onClick={handleDownload}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Image
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 